import * as __field from './fields.internal.js';
import { FieldBuilder } from './fields.js';
import { rawTypes } from './primitives.js';
import { registerType } from './types.js';
export * from './structs.shared.js';
export function struct(structName, fieldDecls, ...options) {
    const opts = options.reduce((acc, opt) => ({ ...acc, ...opt }), {});
    if (typeof this == 'object')
        Object.assign(opts, this);
    // Max alignment of all fields
    let fieldAlignment = 1;
    let size = 0;
    const align = (to) => {
        size = Math.ceil(size / to) * to;
    };
    const fields = [];
    for (const [name, init] of Object.entries(fieldDecls)) {
        if (typeof name == 'number')
            throw new TypeError('Field names can not be numbers');
        const field = __field.init(name, init);
        if (!opts.isPacked)
            align(field.alignment);
        if (opts.isUnion)
            size = Math.max(size, field.type.size);
        else {
            field.offset = size;
            size += field.type.size;
        }
        fields.push(field);
        fieldAlignment = Math.max(fieldAlignment, field.alignment);
    }
    opts.alignment ??= fieldAlignment;
    if (!opts.isPacked)
        align(opts.alignment);
    class _struct extends DataView {
        static name = structName;
        static size = size;
        static alignment = opts.alignment;
        static isUnion = !!opts.isUnion;
        static isDynamic = !!opts.isDynamic;
        static fields = fields;
        static get(buffer, offset) {
            return new this(buffer, offset);
        }
        static set(buffer, offset, value) {
            const source = new Uint8Array(value.buffer, value.byteOffset, this.size);
            const target = new Uint8Array(buffer, offset, this.size);
            if (value.buffer === buffer && value.byteOffset === offset)
                return;
            for (let i = 0; i < this.size; i++)
                target[i] = source[i];
        }
        static [Symbol.toStringTag] = `[struct ${structName}]`;
        constructor(buffer = new ArrayBuffer(size), byteOffset, byteLength) {
            super(buffer, byteOffset, byteLength ?? buffer.byteLength - (byteOffset ?? 0));
            if (!opts.fastFields)
                Object.defineProperties(this, __field.descriptorsOf(this.constructor));
        }
    }
    for (const key of Object.getOwnPropertyNames(DataView.prototype)) {
        if (!key.startsWith('get') && !key.startsWith('set'))
            continue;
        Object.defineProperty(_struct.prototype, key, {
            enumerable: false,
            configurable: false,
            writable: false,
            value: undefined,
        });
    }
    __field.definePrototypeFields(_struct);
    registerType(_struct);
    return _struct;
}
struct.extend = function (base, structName, fieldDecls, ...options) {
    const opts = options.reduce((acc, opt) => ({ ...acc, ...opt }), {});
    if (typeof this == 'object')
        Object.assign(opts, this);
    // Max alignment of all fields
    let fieldAlignment = 1;
    let size = base.size;
    const align = (to) => {
        size = Math.ceil(size / to) * to;
    };
    // Copied, since pushing onto `base.fields` would give the base struct the extension's fields too.
    const fields = [...base.fields];
    for (const [name, init] of Object.entries(fieldDecls)) {
        if (typeof name == 'number')
            throw new TypeError('Field names can not be numbers');
        const field = __field.init(name, init);
        if (!opts.isPacked)
            align(field.alignment);
        if (opts.isUnion)
            size = Math.max(size, field.type.size);
        else {
            field.offset = size;
            size += field.type.size;
        }
        fields.push(field);
        fieldAlignment = Math.max(fieldAlignment, field.alignment);
    }
    opts.alignment ??= fieldAlignment;
    if (!opts.isPacked)
        align(opts.alignment);
    class _struct extends base {
        static name = structName;
        static size = size;
        static alignment = opts.alignment;
        static isUnion = !!opts.isUnion;
        static isDynamic = !!opts.isDynamic;
        static fields = fields;
        static [Symbol.toStringTag] = `[struct ${structName}]`;
    }
    __field.definePrototypeFields(_struct);
    registerType(_struct);
    return _struct;
};
struct.packed = struct.bind({ isPacked: true });
struct.align = function (alignment) {
    return struct.bind({ alignment });
};
struct.dynamic = struct.bind({ isDynamic: true });
export function union(unionName, fieldDecls, ...options) {
    return struct(unionName, fieldDecls, ...options, { isUnion: true });
}
/**
 * Shortcuts for primitive types that allow easily making arrays
 */
export const types = Object.fromEntries(Object.entries(rawTypes).map(([typeName, type]) => [typeName, new FieldBuilder(type, { typeName })]));
